import { eventChannel, EventChannel } from "redux-saga";
import {
  call,
  put,
  take,
  cancelled,
  fork,
  cancel,
  takeLatest,
  select,
} from "redux-saga/effects";
import {
  roomOpen,
  roomClose,
  roomLoaded,
  roomError,
  tablesSet,
  tablesAppend,
  tablesUpdateSeats,
  tablesRemove,
} from "./roomActions";
import { getSocket } from "../../services/socket";

import { Room, TableItem } from "../types";
import { useSelector } from "react-redux";
import { RootState } from "..";
import { useRoute } from "@react-navigation/native";
import { RoomRoute } from "../../app/Components/Middle/RoomTablesSection";

type EmittedEvent =
  | { type: "getTables"; data: TableItem[] }
  | { type: "getTable"; data: TableItem }
  | { type: "joinTable"; data: { tableID: string | number } }
  | { type: "exitTable"; data: { tableID: string | number } }
  | { type: "closeTable"; data: { id: string | number } };

// --- Channel that wires up socket listeners and emits normalized events ---
function socketChannel(
  roomId: string,
  playerId: string,
  username: string
): EventChannel<EmittedEvent> {
  // If your getSocket expects strings, stringify only here.
  const socket = getSocket(playerId, username);

  return eventChannel<EmittedEvent>((emit) => {
    // Join + initial fetch
    // socket.emit("service:enterLobbyRoom", { room: roomId });

    const onGetTables = (packet: any) =>
      emit({ type: "getTables", data: packet?.data ?? [] });
    const onLoadedTable = (packet: any) =>
      emit({ type: "getTable", data: packet?.data });
    const onJoin = (packet: any) =>
      emit({ type: "joinTable", data: packet?.data });
    const onExit = (packet: any) =>
      emit({ type: "exitTable", data: packet?.data });
    const onClosed = (packet: any) =>
      emit({ type: "closeTable", data: packet?.data });

    socket.on("getTables", onGetTables);
    socket.on("getTable", onLoadedTable);
    socket.on("joinTablePublisher", onJoin);
    socket.on("exitTablePublisher", onExit);
    socket.on("getClosedTables", onClosed);

    // Unsubscribe/cleanup
    return () => {
      socket.emit("service:leaveLobbyRoom", { roomId });
      socket.off("getTables", onGetTables);
      socket.off("getTable", onLoadedTable);
      socket.off("joinTablePublisher", onJoin);
      socket.off("exitTablePublisher", onExit);
      socket.off("getClosedTables", onClosed);
    };
  });
}

// --- Saga that consumes the channel and updates Redux state ---
function* handleSocket(roomId: string, playerId: string, username: string) {
  console.log("[Saga] Starting handleSocket", { roomId, playerId, username });
  const chan: EventChannel<EmittedEvent> = yield call(
    socketChannel,
    roomId,
    playerId,
    username
  );

  try {
    while (true) {
      const evt: EmittedEvent = yield take(chan);

      switch (evt.type) {
        case "getTables": {
          const incoming: TableItem[] = Array.isArray(evt.data) ? evt.data : [];
          const current: TableItem[] = yield select((s: any) => s.room.tables);

          const filtered = incoming.filter(
            (t) =>
              String(t.room_id) === String(roomId) &&
              !current.find((c) => c.id === t.id)
          );

          if (filtered.length) {
            if (current.length === 0) yield put(tablesSet(filtered));
            else yield put(tablesAppend(filtered));
          }
          break;
        }

        case "getTable": {
          const table = evt.data;
          if (!table || String(table.room_id) !== String(roomId)) break;

          const current: TableItem[] = yield select((s: any) => s.room.tables);
          if (!current.find((t) => t.id === table.id)) {
            yield put(tablesAppend([table]));
          }
          break;
        }

        case "joinTable": {
          yield put(tablesUpdateSeats({ id: +evt.data.tableID, inc: true }));
          break;
        }

        case "exitTable": {
          yield put(tablesUpdateSeats({ id: +evt.data.tableID, inc: false }));
          break;
        }

        case "closeTable": {
          yield put(tablesRemove(+evt.data.id));
          break;
        }
      }
    }
  } finally {
    if (yield cancelled()) {
      chan.close();
    }
  }
}

// --- Entry worker: fetch room, gate, then fork socket consumer ---
function* openRoomWorker(action: ReturnType<typeof roomOpen>) {
  try {
    // // 1) fetch room by slug (HTTP)
    // const room: Room = yield call(
    //   axiosInstance.getRoomBySlug,
    //   action.payload.roomSlug
    // );

    // 2) gate access (same checks)
    // const user = yield select((s: any) => s.auth.user);
    // if (room.room_type === "vip" && !user?.is_vip_user) {
    //   throw new Error("vip_denied");
    // }
    // if (
    //   room.min_user_level > (user?.gamesData?.svarka_level ?? 0) &&
    //   !user?.vipRooms?.includes(room.id)
    // ) {
    //   throw new Error("level_denied");
    // }

    // 3) store room in redux
    // yield put(roomLoaded(room));

    // 4) normalize room id to number if needed and start socket task
    // const roomId: string = String(room.id);
    // const playerId: string = user?.id ?? user?.playerId ?? "anonymous";
    // const username: string = user?.username ?? "guest";

    const room: Room = {
      id: String(action.payload.roomId), // backend expects this to match actual room id string
      // slug: action.payload.roomSlug,
      // name: action.payload.roomSlug,
      slug: "sofia",
      name: "sofia",
      room_type: "normal",
      min_user_level: 1,
    };
    yield put(roomLoaded(room));
    // const roomId: string = String(
    //   "gameroom-63ea8c74-6bdc-4c4e-9662-0c811ac3404b"
    // );
    const { params } = useRoute<RoomRoute>();
    const { roomId } = params;
    const auth = useSelector((state: RootState) => state.auth);
    const playerId = auth.user?.id || "anonymous";
    const username = auth.user?.username || "anonymous";

    const task = yield fork(handleSocket, roomId, playerId, username);

    // 5) wait until close, then cancel the socket task
    yield take(roomClose.type);
    yield cancel(task);
  } catch (e: any) {
    yield put(roomError(e?.message || "room_failed"));
  }
}

export function* roomSocketSaga() {
  yield takeLatest(roomOpen.type, openRoomWorker);
}
